"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import LeadsAnalytics from "./LeadsAnalytics";
import {
  PlusIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const API = "https://erp-xe9u.onrender.com/api";

// NOTE: restrict this key by HTTP referrer in Google Cloud, or move it to an env var.
const GOOGLE_PLACES_KEY = "AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg";

const LEAD_SOURCE_CATEGORIES = [
  "Public Tender Platform",
  "Direct Invite",
  "Referral",
  "Outbound Prospecting",
  "Repeat Client",
];

const DISQUALIFICATION_CATEGORIES = [
  "Outside Niche",
  "Wrong Geography",
  "Too Small",
  "Too Big",
  "Bad GC History",
  "Schedule Conflict",
  "Incomplete Documents",
  "Estimation Capacity Issue",
  "Reviewed Estimate Late",
];

const STATUS_FILTERS = [
  { value: "unqualified", label: "Unqualified" },
  { value: "qualified", label: "Qualified" },
  { value: "disqualified", label: "Disqualified" },
  { value: "all", label: "All" },
];

const EMPTY_ADDRESS = {
  formattedAddress: "",
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  latitude: null,
  longitude: null,
  placeId: "",
};

const EMPTY_FORM = {
  contactId: null,
  jobTitle: "",
  jobAddress: { ...EMPTY_ADDRESS },
  description: "",
  drawingLinks: [""],
  status: "unqualified",
  sourceCategory: "",
  attachments: [],
};

const DEFAULT_DISQUALIFY_EMAIL = {
  sendEmail: true,
  to: "",
  subject: "Lead Disqualification Notification",
  body: "",
};

const SORT_FIELD = "createdAt";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 30,
  }),
};

const inputClass =
  "block w-full rounded-lg bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none";

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors disabled:cursor-not-allowed disabled:opacity-50";

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  red: {
    wrap: "bg-red-500/10 text-red-400 ring-red-500/20",
    dot: "fill-red-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      <svg
        viewBox="0 0 6 6"
        aria-hidden="true"
        className={classNames("size-1.5", t.dot)}
      >
        <circle r={3} cx={3} cy={3} />
      </svg>
      {children}
    </span>
  );
}

const leadStatusTone = (s) =>
  s === "qualified" ? "green" : s === "disqualified" ? "red" : "gray";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "N/A");

function Section({ title, children, className = "" }) {
  return (
    <section
      className={classNames(
        "rounded-xl bg-gray-800 ring-1 ring-white/10",
        className,
      )}
    >
      {title && (
        <div className="border-b border-white/10 px-5 py-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-orange-400">
            {title}
          </h3>
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
      {children}
    </label>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, maxWidth = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className={classNames(
          "w-full rounded-2xl bg-gray-800 shadow-2xl ring-1 ring-white/10",
          maxWidth,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function IconButton({ onClick, title, tone = "gray", children }) {
  const tones = {
    gray: "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
    green: "text-green-400 hover:bg-green-500/10",
    red: "text-red-400 hover:bg-red-500/10",
    orange: "text-orange-400 hover:bg-orange-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={classNames("rounded-lg p-1.5 transition-colors", tones[tone])}
    >
      {children}
    </button>
  );
}

export default function LeadsTab() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isQualifying, setIsQualifying] = useState(false);
  const [automatedEmail, setAutomatedEmail] = useState(false);
  const [isDisqualifying, setIsDisqualifying] = useState(false);
  const [qualifyLeadId, setQualifyLeadId] = useState(null);
  const [disqualifyLeadId, setDisqualifyLeadId] = useState(null);
  const [disqualifyEmailForm, setDisqualifyEmailForm] = useState({
    ...DEFAULT_DISQUALIFY_EMAIL,
  });
  const [filter, setFilter] = useState(() => {
    return sessionStorage.getItem("leadsFilter") || "unqualified";
  });
  const [viewMode, setViewMode] = useState("list"); // "list" | "analytics"
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(50);
  const [sortOrder, setSortOrder] = useState("desc");
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [qualifyFormData, setQualifyFormData] = useState({
    estimator: null,
    estimateDueDate: "",
    qualificationReason: "",
  });
  const [disqualifyFormData, setDisqualifyFormData] = useState({
    disqualificationReason: "",
    disqualificationCategory: "",
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    leadId: null,
  });

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));
  const showAlert = (type, title, message, onConfirm = closeAlert) =>
    setAlert({ open: true, type, title, message, onConfirm });
  const showError = (message) => showAlert("error", "Error", message);

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/leads?page=${currentPage}&limit=${itemsPerPage}&status=${filter}&sortField=${SORT_FIELD}&sortOrder=${sortOrder}`,
        authConfig(),
      );
      setLeads(response.data.leads || []);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
      setError(null);
    } catch (err) {
      setError(
        "Failed to load leads: " + (err.response?.data?.message || err.message),
      );
      console.error("Fetch leads error:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  // Reference data: loaded once
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [
          contactsResponse,
          accountsResponse,
          companyResponse,
          usersResponse,
        ] = await Promise.all([
          axios.get(`${API}/contacts`, authConfig()),
          axios.get(`${API}/accounts`, authConfig()),
          axios.get(`${API}/company-profile`, authConfig()),
          axios.get(`${API}/users`, authConfig()),
        ]);
        setContacts(contactsResponse.data);
        setAccounts(accountsResponse.data);
        setAutomatedEmail(
          companyResponse.data.plugins?.automatedLeads?.aliasEmail,
        );
        setUsers(
          usersResponse.data.filter(
            (user) => user.role === "project_manager" || user.role === "admin",
          ),
        );
      } catch (err) {
        setError(
          "Failed to load data: " +
            (err.response?.data?.message || err.message),
        );
        console.error("Reference data error:", err.response?.data || err);
      }
    };
    fetchReferenceData();
  }, []);

  // Leads: reload on page / filter / sort change
  useEffect(() => {
    fetchLeads();
  }, [currentPage, filter, sortOrder]);

  useEffect(() => {
    sessionStorage.setItem("leadsFilter", filter);
  }, [filter]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQualifyInputChange = (e) => {
    const { name, value } = e.target;
    setQualifyFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDisqualifyInputChange = (e) => {
    const { name, value } = e.target;
    setDisqualifyFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDisqualifyEmailInputChange = (e) => {
    const { name, value } = e.target;
    setDisqualifyEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      contactId: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleEstimatorChange = (selectedOption) => {
    setQualifyFormData((prev) => ({
      ...prev,
      estimator: selectedOption ? selectedOption.value : null,
    }));
  };

  const applyAddress = (jobAddress, streetName) => {
    setFormData((prev) => ({
      ...prev,
      jobAddress,
      // Default the job title to the street name if empty or previously an address
      jobTitle:
        !prev.jobTitle || prev.jobTitle === prev.jobAddress.formattedAddress
          ? streetName
          : prev.jobTitle,
    }));
  };

  const handleAddressChange = async (place) => {
    if (!place || !place.value || !place.value.place_id) {
      return showError("Please select a valid address from the dropdown.");
    }

    const streetFromLabel = place.label ? place.label.split(",")[0].trim() : "";
    const fallback = () =>
      applyAddress(
        {
          ...EMPTY_ADDRESS,
          formattedAddress: place.label || "",
          street: streetFromLabel,
          placeId: place.value.place_id || "",
        },
        streetFromLabel,
      );

    try {
      const response = await axios.get(
        `${API}/leads/address/${place.value.place_id}`,
        authConfig(),
      );

      if (response.data.success && response.data.address) {
        const { address } = response.data;
        applyAddress(
          {
            formattedAddress: address.formattedAddress || "",
            street: address.street || "",
            city: address.city || "",
            state: address.state || "",
            postalCode: address.postalCode || "",
            country: address.country || "",
            latitude: address.latitude ?? null,
            longitude: address.longitude ?? null,
            placeId: address.placeId || place.value.place_id,
          },
          address.street || "",
        );
      } else {
        showError(
          `Failed to fetch address details: ${
            response.data.message || "No address data returned"
          }`,
        );
        fallback();
      }
    } catch (err) {
      showError(
        `Error fetching address details: ${err.message}. Please try again or contact support.`,
      );
      fallback();
    }
  };

  const handleDrawingLinkChange = (index, value) => {
    setFormData((prev) => {
      const drawingLinks = [...prev.drawingLinks];
      drawingLinks[index] = value;
      return { ...prev, drawingLinks };
    });
  };

  const addDrawingLink = () => {
    setFormData((prev) => ({
      ...prev,
      drawingLinks: [...prev.drawingLinks, ""],
    }));
  };

  const removeDrawingLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      drawingLinks: prev.drawingLinks.filter((_, i) => i !== index),
    }));
  };

  const fetchDisqualificationEmailTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/company-profile/automated-leads/disqualification-email`,
        authConfig(),
      );
      return response.data.disqualificationEmailBody;
    } catch (err) {
      throw new Error(
        "Failed to fetch disqualification email template: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleOpenDisqualifyModal = async (leadId) => {
    try {
      const lead = leads.find((l) => l._id === leadId);
      if (!lead || !lead.contactId) {
        return showError("Lead or contact information not found.");
      }

      const template = await fetchDisqualificationEmailTemplate();
      const emailBody = `Hello ${lead.contactId.firstName},\n\n${template}`;
      setDisqualifyEmailForm({
        sendEmail: true,
        to: lead.contactId.email,
        subject: lead.jobTitle,
        body: emailBody,
      });
      setDisqualifyLeadId(leadId);
      setDisqualifyFormData({
        disqualificationReason: "",
        disqualificationCategory: "",
      });
      setIsDisqualifying(true);
    } catch (err) {
      showError(err.message);
    }
  };

  const closeDisqualifyModal = () => {
    setIsDisqualifying(false);
    setDisqualifyLeadId(null);
    setDisqualifyEmailForm({ ...DEFAULT_DISQUALIFY_EMAIL });
  };

  const handleDisqualify = async (e) => {
    e.preventDefault();
    if (!disqualifyFormData.disqualificationReason) {
      return showError("Please provide a disqualification reason.");
    }

    try {
      const response = await axios.post(
        `${API}/leads/disqualify/${disqualifyLeadId}`,
        {
          disqualificationReason: disqualifyFormData.disqualificationReason,
          disqualificationCategory: disqualifyFormData.disqualificationCategory,
          sendEmail: disqualifyEmailForm.sendEmail,
          emailSubject: disqualifyEmailForm.sendEmail
            ? disqualifyEmailForm.subject
            : undefined,
          emailBody: disqualifyEmailForm.sendEmail
            ? disqualifyEmailForm.body
            : undefined,
        },
        authConfig(),
      );

      closeDisqualifyModal();
      showAlert("success", "Success", response.data.message, () => {
        closeAlert();
        fetchLeads();
      });
    } catch (err) {
      showError(
        "Failed to disqualify lead: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const validateJobTitle = (jobTitle) => {
    const addressPattern =
      /\d+\s+[a-zA-Z\s]+(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard)/i;
    return !addressPattern.test(jobTitle);
  };

  const resetCreateForm = () => {
    setIsCreating(false);
    setFormData({ ...EMPTY_FORM, jobAddress: { ...EMPTY_ADDRESS } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contactId) return showError("Please select a contact.");
    if (!formData.jobTitle) return showError("Please enter a job title.");
    if (!validateJobTitle(formData.jobTitle)) {
      return showError(
        "Job title appears to be an address. Please enter a valid job title.",
      );
    }
    if (!formData.jobAddress.formattedAddress) {
      return showError("Please select a valid job address.");
    }

    try {
      const contact = contacts.find((c) => c._id === formData.contactId);
      if (!contact) return showError("Selected contact not found.");

      const companyId = contact.relatedAccount
        ? contact.relatedAccount.$oid || contact.relatedAccount
        : null;

      const formDataToSend = new FormData();
      formDataToSend.append("contactId", formData.contactId || "");
      formDataToSend.append("jobTitle", formData.jobTitle || "");
      formDataToSend.append("jobAddress", JSON.stringify(formData.jobAddress));
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append(
        "drawingLinks",
        JSON.stringify(formData.drawingLinks.filter((l) => l.trim() !== "")),
      );
      formDataToSend.append("status", formData.status || "unqualified");
      formDataToSend.append("sourceCategory", formData.sourceCategory || "");
      if (companyId) {
        formDataToSend.append("company", companyId);
      }

      await axios.post(`${API}/leads`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data],
      });

      resetCreateForm();
      setCurrentPage(1);
      showAlert("success", "Success", "Lead created successfully!", () => {
        closeAlert();
        fetchLeads();
      });
    } catch (err) {
      console.error("Create lead error:", err.response?.data || err);
      showError(
        `Failed to create lead: ${err.response?.data?.message || err.message}`,
      );
    }
  };

  const handleOpenQualifyModal = (leadId) => {
    setQualifyLeadId(leadId);
    setQualifyFormData({
      estimator: null,
      estimateDueDate: "",
      qualificationReason: "",
    });
    setIsQualifying(true);
  };

  const handleQualify = async (e) => {
    e.preventDefault();
    if (!qualifyFormData.estimator)
      return showError("Please select an estimator.");
    if (!qualifyFormData.estimateDueDate) {
      return showError("Please select an estimate due date.");
    }
    if (!qualifyFormData.qualificationReason) {
      return showError("Please provide a qualification reason.");
    }

    try {
      const response = await axios.post(
        `${API}/leads/qualify/${qualifyLeadId}`,
        qualifyFormData,
        authConfig(),
      );

      setIsQualifying(false);
      setQualifyLeadId(null);
      showAlert("success", "Success", "Lead qualified as opportunity!", () => {
        closeAlert();
        fetchLeads();
        navigate(`/Opportunities/${response.data.opportunity._id}`, {
          state: { newOpportunity: response.data.opportunity },
        });
      });
    } catch (err) {
      showError(
        "Failed to qualify lead: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleDeleteConfirm = (leadId) => {
    setDeleteConfirm({ open: true, leadId });
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/leads/${deleteConfirm.leadId}`, authConfig());
      setTotalItems((prev) => prev - 1);
      setDeleteConfirm({ open: false, leadId: null });
      if (leads.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        await fetchLeads();
      }
      showAlert("success", "Success", "Lead deleted successfully!");
    } catch (err) {
      setDeleteConfirm({ open: false, leadId: null });
      showError(
        "Failed to delete lead: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleView = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const handleSort = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setCurrentPage(1);
  };

  const contactOptions = contacts.map((contact) => ({
    value: contact._id,
    label: `${contact.firstName} ${contact.lastName}`,
  }));

  const estimatorOptions = users.map((user) => ({
    value: user._id,
    label: user.displayName || user.email,
  }));

  const getAccountName = (contactId) => {
    const contact = contacts.find((c) => c._id === contactId);
    if (!contact || !contact.relatedAccount) return "None";
    const accountId = contact.relatedAccount.$oid || contact.relatedAccount;
    const account = accounts.find((a) => a._id === accountId);
    return account ? account.companyName : "None";
  };

  const rangeStart =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="mx-auto max-w-full px-4 py-6">
      <div className="rounded-2xl bg-gray-900 p-4 shadow-xl ring-1 ring-white/10 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
                Pipeline
              </p>
              <h1 className="text-2xl font-bold text-white">Leads</h1>
            </div>
            <div className="flex rounded-lg bg-gray-800 p-1 ring-1 ring-white/10">
              {["list", "analytics"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={classNames(
                    "rounded-md px-3 py-1.5 text-sm font-semibold capitalize transition-colors",
                    viewMode === mode
                      ? "bg-orange-500 text-white shadow"
                      : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                  )}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {viewMode === "list" && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div
                className="flex flex-wrap gap-1 rounded-lg bg-gray-800 p-1 ring-1 ring-white/10"
                role="tablist"
                aria-label="Filter by status"
              >
                {STATUS_FILTERS.map((opt) => (
                  <button
                    key={opt.value}
                    role="tab"
                    aria-selected={filter === opt.value}
                    onClick={() => {
                      setFilter(opt.value);
                      setCurrentPage(1);
                    }}
                    className={classNames(
                      "rounded-md px-3 py-1.5 text-sm font-semibold transition-colors",
                      filter === opt.value
                        ? "bg-gray-600 text-white shadow"
                        : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setIsCreating(true)}
                className={btnPrimary}
              >
                <PlusIcon className="size-4" />
                Create Lead
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/20">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-300 hover:text-red-100"
              aria-label="Dismiss"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
        )}

        {viewMode === "list" ? (
          <>
            {automatedEmail ? (
              <div className="mb-4 flex items-center gap-3 rounded-lg bg-gray-800 px-4 py-3 ring-1 ring-white/10">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-400">
                  <EnvelopeIcon className="size-4" />
                </span>
                <div className="min-w-0 text-sm">
                  <span className="text-gray-400">
                    Automated inbound email:{" "}
                  </span>
                  <span className="break-all font-semibold text-gray-100">
                    {automatedEmail}
                  </span>
                </div>
              </div>
            ) : null}

            {/* Create form */}
            {isCreating && (
              <form onSubmit={handleSubmit} className="mb-6 space-y-6">
                <Section title="New Lead">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField label="Contact">
                      <Select
                        options={contactOptions}
                        value={
                          contactOptions.find(
                            (option) => option.value === formData.contactId,
                          ) || null
                        }
                        onChange={handleContactChange}
                        placeholder="Select a contact..."
                        isClearable
                        styles={customStyles}
                      />
                    </FormField>
                    <FormField label="Related Account">
                      <p className="py-2 text-sm text-gray-200">
                        {getAccountName(formData.contactId)}
                      </p>
                    </FormField>
                    <FormField label="Job Address" className="md:col-span-2">
                      <GooglePlacesAutocomplete
                        autocompletionRequest={{
                          componentRestrictions: { country: ["ca"] },
                        }}
                        apiKey={GOOGLE_PLACES_KEY}
                        selectProps={{
                          onChange: handleAddressChange,
                          placeholder:
                            "Enter a job address (e.g., 123 Main St, Toronto, ON)",
                          value: formData.jobAddress.formattedAddress
                            ? {
                                label: formData.jobAddress.formattedAddress,
                                value: {
                                  description:
                                    formData.jobAddress.formattedAddress,
                                  place_id: formData.jobAddress.placeId || "",
                                },
                              }
                            : null,
                          styles: customStyles,
                        }}
                      />
                    </FormField>
                    <FormField label="Job Title">
                      <input
                        type="text"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        className={classNames(
                          inputClass,
                          formData.jobTitle &&
                            formData.jobTitle ===
                              formData.jobAddress.formattedAddress
                            ? "ring-2 ring-orange-400"
                            : "",
                        )}
                        required
                        placeholder="Enter job title"
                      />
                      {formData.jobTitle &&
                        formData.jobTitle ===
                          formData.jobAddress.formattedAddress && (
                          <p className="mt-1 text-xs text-orange-400">
                            Job title is set to the address. Please edit it to a
                            descriptive title.
                          </p>
                        )}
                    </FormField>
                    <FormField label="Lead Source">
                      <select
                        value={formData.sourceCategory}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            sourceCategory: e.target.value,
                          }))
                        }
                        className={inputClass}
                      >
                        <option value="">Select a source...</option>
                        {LEAD_SOURCE_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Status">
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className={inputClass}
                      >
                        <option value="unqualified">Unqualified</option>
                        <option value="qualified">Qualified</option>
                        <option value="disqualified">Disqualified</option>
                      </select>
                    </FormField>
                    <FormField label="Description" className="md:col-span-2">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label="Drawing Links" className="md:col-span-2">
                      <div className="space-y-2">
                        {formData.drawingLinks.map((link, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="url"
                              value={link}
                              onChange={(e) =>
                                handleDrawingLinkChange(index, e.target.value)
                              }
                              className={inputClass}
                              placeholder="https://example.com"
                            />
                            {formData.drawingLinks.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeDrawingLink(index)}
                                className="rounded-lg p-2 text-red-400 hover:bg-red-500/10"
                                aria-label="Remove link"
                              >
                                <TrashIcon className="size-5" />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addDrawingLink}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-gray-700 px-3 py-1.5 text-sm font-medium text-orange-400 hover:bg-gray-600"
                        >
                          <PlusIcon className="size-4" />
                          Add Drawing Link
                        </button>
                      </div>
                    </FormField>
                  </div>
                </Section>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={resetCreateForm}
                    className={btnSecondary}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={btnPrimary}>
                    Create Lead
                  </button>
                </div>
              </form>
            )}

            {/* Qualify modal */}
            {isQualifying && (
              <Modal
                title="Qualify Lead"
                onClose={() => {
                  setIsQualifying(false);
                  setQualifyLeadId(null);
                }}
              >
                <form onSubmit={handleQualify} className="space-y-4">
                  <FormField label="Estimator (Admin / PM)">
                    <Select
                      options={estimatorOptions}
                      value={
                        estimatorOptions.find(
                          (option) =>
                            option.value === qualifyFormData.estimator,
                        ) || null
                      }
                      onChange={handleEstimatorChange}
                      placeholder="Select an estimator..."
                      isClearable
                      styles={customStyles}
                    />
                  </FormField>
                  <FormField label="Estimate Due Date">
                    <input
                      type="date"
                      required
                      name="estimateDueDate"
                      value={qualifyFormData.estimateDueDate}
                      onChange={handleQualifyInputChange}
                      className={inputClass}
                    />
                  </FormField>
                  <FormField label="Qualification Reason">
                    <textarea
                      name="qualificationReason"
                      value={qualifyFormData.qualificationReason}
                      onChange={handleQualifyInputChange}
                      className={inputClass}
                      rows={4}
                      required
                      placeholder="Enter the reason for qualifying this lead..."
                    />
                  </FormField>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsQualifying(false);
                        setQualifyLeadId(null);
                      }}
                      className={btnSecondary}
                    >
                      Cancel
                    </button>
                    <button type="submit" className={btnPrimary}>
                      Qualify
                    </button>
                  </div>
                </form>
              </Modal>
            )}

            {/* Disqualify modal */}
            {isDisqualifying && (
              <Modal title="Disqualify Lead" onClose={closeDisqualifyModal}>
                <form onSubmit={handleDisqualify} className="space-y-4">
                  <FormField label="Disqualification Category">
                    <select
                      name="disqualificationCategory"
                      value={disqualifyFormData.disqualificationCategory}
                      onChange={handleDisqualifyInputChange}
                      required
                      className={inputClass}
                    >
                      <option value="">Select a category...</option>
                      {DISQUALIFICATION_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="Disqualification Reason">
                    <textarea
                      name="disqualificationReason"
                      value={disqualifyFormData.disqualificationReason}
                      onChange={handleDisqualifyInputChange}
                      className={inputClass}
                      rows={4}
                      required
                      placeholder="Enter the reason for disqualifying this lead..."
                    />
                  </FormField>

                  <label className="flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2.5 text-sm font-medium text-gray-200 ring-1 ring-white/10">
                    <input
                      type="checkbox"
                      checked={disqualifyEmailForm.sendEmail}
                      onChange={(e) =>
                        setDisqualifyEmailForm((prev) => ({
                          ...prev,
                          sendEmail: e.target.checked,
                        }))
                      }
                      className="size-4 rounded border-gray-600 bg-gray-700 text-orange-500 focus:ring-orange-500"
                    />
                    Send disqualification email
                  </label>

                  {disqualifyEmailForm.sendEmail && (
                    <div className="space-y-4 rounded-lg bg-gray-900/60 p-4 ring-1 ring-white/10">
                      <FormField label="To">
                        <input
                          type="email"
                          name="to"
                          value={disqualifyEmailForm.to}
                          disabled
                          className={classNames(
                            inputClass,
                            "cursor-not-allowed opacity-60",
                          )}
                        />
                      </FormField>
                      <FormField label="Subject">
                        <input
                          type="text"
                          name="subject"
                          value={disqualifyEmailForm.subject}
                          onChange={handleDisqualifyEmailInputChange}
                          className={inputClass}
                        />
                      </FormField>
                      <FormField label="Email Body">
                        <textarea
                          name="body"
                          value={disqualifyEmailForm.body}
                          onChange={handleDisqualifyEmailInputChange}
                          className={inputClass}
                          rows={6}
                        />
                      </FormField>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={closeDisqualifyModal}
                      className={btnSecondary}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors"
                    >
                      Disqualify
                    </button>
                  </div>
                </form>
              </Modal>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-xl bg-gray-800 ring-1 ring-white/10">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/10">
                  <thead className="bg-gray-800">
                    <tr>
                      {["Lead", "Contact", "Account", "Source", "Status"].map(
                        (h) => (
                          <th
                            key={h}
                            scope="col"
                            className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
                          >
                            {h}
                          </th>
                        ),
                      )}
                      <th
                        scope="col"
                        className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
                      >
                        <button
                          onClick={handleSort}
                          className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-100"
                        >
                          Created
                          {sortOrder === "desc" ? (
                            <ChevronDownIcon className="size-4 text-orange-400" />
                          ) : (
                            <ChevronUpIcon className="size-4 text-orange-400" />
                          )}
                        </button>
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {loading ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-4 py-10 text-center text-sm text-gray-400"
                        >
                          Loading leads...
                        </td>
                      </tr>
                    ) : leads.length === 0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-4 py-10 text-center text-sm text-gray-400"
                        >
                          No leads found
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => {
                        const accountName = getAccountName(
                          lead.contactId?._id || lead.contactId,
                        );
                        return (
                          <tr
                            key={lead._id}
                            className="cursor-pointer transition-colors hover:bg-gray-700/50"
                            onClick={() => handleView(lead._id)}
                            title={`View details for ${lead.jobTitle || "N/A"}`}
                          >
                            <td className="max-w-xs px-4 py-3">
                              <p className="truncate text-sm font-semibold text-gray-100">
                                {lead.jobTitle || "N/A"}
                              </p>
                              {lead.jobAddress?.formattedAddress && (
                                <p className="truncate text-xs text-gray-400">
                                  {lead.jobAddress.formattedAddress}
                                </p>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                              {lead.contactId?.firstName &&
                              lead.contactId?.lastName
                                ? `${lead.contactId.firstName} ${lead.contactId.lastName}`
                                : "Unknown Contact"}
                            </td>
                            <td
                              className="max-w-[12rem] truncate px-4 py-3 text-sm text-gray-300"
                              title={accountName}
                            >
                              {accountName}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                              {lead.sourceCategory || "—"}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <Badge tone={leadStatusTone(lead.status)}>
                                {capitalize(lead.status)}
                              </Badge>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                              {lead.createdAt
                                ? new Date(lead.createdAt).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td
                              className="whitespace-nowrap px-4 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-end gap-0.5">
                                <IconButton
                                  title="View"
                                  tone="orange"
                                  onClick={() => handleView(lead._id)}
                                >
                                  <EyeIcon className="size-5" />
                                </IconButton>
                                {lead.status !== "qualified" && (
                                  <IconButton
                                    title="Qualify"
                                    tone="green"
                                    onClick={() =>
                                      handleOpenQualifyModal(lead._id)
                                    }
                                  >
                                    <CheckCircleIcon className="size-5" />
                                  </IconButton>
                                )}
                                {lead.status !== "disqualified" && (
                                  <IconButton
                                    title="Disqualify"
                                    tone="red"
                                    onClick={() =>
                                      handleOpenDisqualifyModal(lead._id)
                                    }
                                  >
                                    <XCircleIcon className="size-5" />
                                  </IconButton>
                                )}
                                <IconButton
                                  title="Delete"
                                  tone="red"
                                  onClick={() => handleDeleteConfirm(lead._id)}
                                >
                                  <TrashIcon className="size-5" />
                                </IconButton>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-sm text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-200">
                  {rangeStart}–{rangeEnd}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-200">
                  {totalItems}
                </span>{" "}
                leads
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className={btnSecondary}
                >
                  <ChevronLeftIcon className="size-4" />
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className={btnSecondary}
                >
                  Next
                  <ChevronRightIcon className="size-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <LeadsAnalytics />
        )}
      </div>

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
        onConfirm={alert.onConfirm}
      />
      <AlertDialog
        open={deleteConfirm.open}
        type="warning"
        title="Confirm Delete"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        onClose={() => setDeleteConfirm({ open: false, leadId: null })}
        onConfirm={handleDelete}
        showCancel
      />
    </div>
  );
}
